import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ClockIcon,
  LockClosedIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Layout } from '../components/shared/Layout';

export function HomePage() {
  return (
    <Layout>
      {/* Hero Section */}
      <div className="text-center py-12 lg:py-20">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
          Verify Your Marital Status
          <span className="text-primary-600"> Securely</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
          Get official marital status verification from the South African Department of Home Affairs.
          Quick, secure, and POPIA compliant.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link to="/register" className="btn-primary text-lg px-8 py-3">
            Start Verification
          </Link>
          <Link to="/login" className="btn-secondary text-lg px-8 py-3">
            Sign In
          </Link>
        </div>
      </div>

      {/* Features */}
      <div className="py-12 lg:py-20 border-t border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
          Why Choose Am I Secretly Married?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-100 rounded-full mb-4">
              <ClockIcon className="h-7 w-7 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick Results</h3>
            <p className="text-gray-600">
              Get your verified marital status within 24-48 hours after payment confirmation.
            </p>
          </div>

          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-100 rounded-full mb-4">
              <LockClosedIcon className="h-7 w-7 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
            <p className="text-gray-600">
              Your data is encrypted and automatically deleted after 30 days. Fully POPIA compliant.
            </p>
          </div>

          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-100 rounded-full mb-4">
              <DocumentCheckIcon className="h-7 w-7 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Official Source</h3>
            <p className="text-gray-600">
              Information sourced directly from the Department of Home Affairs database.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-12 lg:py-20 border-t border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">How It Works</h2>
        <div className="max-w-3xl mx-auto">
          <div className="space-y-8">
            {[
              {
                step: 1,
                title: 'Create an Account',
                description: 'Register with your email and verify your phone number.',
              },
              {
                step: 2,
                title: 'Provide Your Details',
                description: 'Enter your personal information and upload your ID document and selfie.',
              },
              {
                step: 3,
                title: 'Make Payment',
                description: 'Pay securely via credit card or EFT through PayFast.',
              },
              {
                step: 4,
                title: 'Verification Review',
                description: 'Our team reviews your application and verifies your identity.',
              },
              {
                step: 5,
                title: 'Receive Results',
                description: 'Your verified marital status is available in your account.',
              },
            ].map((item, _index) => (
              <div key={item.step} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="py-12 lg:py-20 border-t border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">Simple Pricing</h2>
        <div className="max-w-md mx-auto">
          <div className="card border-2 border-primary-500">
            <div className="text-center">
              <p className="text-sm font-medium text-primary-600 mb-2">One-Time Fee</p>
              <div className="flex items-baseline justify-center">
                <span className="text-5xl font-bold text-gray-900">R150</span>
              </div>
              <p className="text-gray-600 mt-2">per verification</p>
            </div>
            <ul className="mt-8 space-y-3">
              {[
                'Official Home Affairs verification',
                'Identity verification included',
                'Secure document handling',
                'Results within 24-48 hours',
                'Email notifications',
                'POPIA compliant',
              ].map((feature) => (
                <li key={feature} className="flex items-center text-gray-600">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              className="btn-primary w-full mt-8 py-3 text-center block"
            >
              Get Started
            </Link>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="py-12 border-t border-gray-200">
        <div className="flex flex-wrap justify-center items-center gap-8 text-gray-400">
          <div className="flex items-center space-x-2">
            <ShieldCheckIcon className="h-8 w-8" />
            <span className="font-medium">TLS 1.3 Secured</span>
          </div>
          <div className="flex items-center space-x-2">
            <LockClosedIcon className="h-8 w-8" />
            <span className="font-medium">POPIA Compliant</span>
          </div>
          <div className="flex items-center space-x-2">
            <DocumentCheckIcon className="h-8 w-8" />
            <span className="font-medium">DHA Authorized</span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
